import React, { useMemo } from 'react';
import {
    GenericTable,
    GenericTableColumn,
} from '@ui-components/Table/GenericTable';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericModal from '@ui-components/Modal/GenericModal';
import GenericCard from '@ui-components/Card/GenericCard';
import {
    BADGE_TRIGGER_STATE,
    EventStatus,
    IncidentGroup,
    NetworkEventRow,
    formatDuration,
    formatTimestamp,
    formatTimeOnly,
    getEventTypeLabel,
} from './networkAlertTypes';

const STATUS_TAG_VARIANT: Record<EventStatus, 'error' | 'success' | 'warning'> = {
    'Down': 'error',
    'Resolved': 'success',
    'Up': 'success',
    'Warning': 'warning',
};

/**
 * Build the set of `traffic_resumed` event IDs that are paired with a
 * resolved `zero_traffic` in this incident. We use it to relabel the Type
 * tag of those recovery rows from "Recovered" to "Idle" \u2014 the recovery is
 * conceptually part of the idle's lifecycle, and showing it under "Idle"
 * keeps the timeline cohesive (idle started \u2192 idle resolved \u2192 traffic
 * came back at this exact time).
 */
function buildAbsorbedRecoveryIds(events: NetworkEventRow[]): Set<string> {
    const absorbed = new Set<string>();
    for (const evt of events) {
        if (
            evt.eventType === 'zero_traffic' &&
            evt.status === 'Resolved' &&
            evt.resolvedByEventId
        ) {
            absorbed.add(evt.resolvedByEventId);
        }
    }
    return absorbed;
}

function buildEventDetailColumns(
    absorbedRecoveryIds: Set<string>
): GenericTableColumn<NetworkEventRow>[] {
    return [
        {
            header: 'Time',
            accessor: 'timestamp' as keyof NetworkEventRow,
            renderCell: (row: NetworkEventRow) => (
                <span className="text-xs font-mono text-semantic-text-secondary whitespace-nowrap">
                    {formatTimeOnly(row.timestamp)}
                </span>
            ),
        },
        {
            header: 'Type',
            accessor: 'eventType' as keyof NetworkEventRow,
            renderCell: (row: NetworkEventRow) => {
                // A traffic_resumed paired with a resolved idle renders as
                // "Idle" so the recovery row groups with the idle's
                // lifecycle rather than appearing as a separate "Recovered"
                // type. The actual eventType in the payload is unchanged.
                const label = absorbedRecoveryIds.has(row.id)
                    ? getEventTypeLabel('zero_traffic')
                    : getEventTypeLabel(row.eventType);
                return (
                    <GenericTag
                        label={label}
                        variant="neutral"
                        size="small"
                        rounded="medium"
                    />
                );
            },
        },
        {
            header: 'Trigger Event',
            accessor: 'badge' as keyof NetworkEventRow,
            renderCell: (row: NetworkEventRow) => {
                if (!row.badge) return <span className="text-xs text-semantic-text-secondary">{'\u2014'}</span>;
                const trigger = BADGE_TRIGGER_STATE[row.badge];
                if (!trigger) return <span className="text-xs text-semantic-text-secondary">{'\u2014'}</span>;
                return (
                    <GenericTag
                        label={trigger.label}
                        variant={trigger.variant}
                        size="small"
                        rounded="medium"
                    />
                );
            },
        },
        {
            header: 'Current Status',
            accessor: 'status' as keyof NetworkEventRow,
            renderCell: (row: NetworkEventRow) => (
                <GenericTag label={row.status} variant={STATUS_TAG_VARIANT[row.status] || 'neutral'} size="small" rounded="medium" />
            ),
        },
        {
            header: 'Neighbor',
            accessor: 'detail' as keyof NetworkEventRow,
            renderCell: (row: NetworkEventRow) => {
                const neighbor = row.detail?.neighbor || row.detail?.peer || null;
                return (
                    <span className="text-xs font-mono text-semantic-text-secondary whitespace-nowrap">
                        {neighbor || '\u2014'}
                    </span>
                );
            },
        },
        {
            header: 'Reason',
            accessor: 'reason' as keyof NetworkEventRow,
            width: '200px',
            wrapMode: 'wrap',
            renderCell: (row: NetworkEventRow) => (
                <span className="text-xs text-semantic-text-secondary leading-relaxed">
                    {row.reason || '\u2014'}
                    {row.note && <span className="italic ml-1">({row.note})</span>}
                </span>
            ),
        },
    ];
}

interface IncidentDetailModalProps {
    incident: IncidentGroup | null;
    onClose: () => void;
}

const IncidentDetailModal: React.FC<IncidentDetailModalProps> = ({ incident, onClose }) => {
    const eventDetailColumns = useMemo(() => {
        const absorbed = incident
            ? buildAbsorbedRecoveryIds(incident.events)
            : new Set<string>();
        return buildEventDetailColumns(absorbed);
    }, [incident]);

    if (!incident) return null;
    return (
        <GenericModal
            isOpen={!!incident}
            onClose={onClose}
            title={`Incident Details \u2014 ${formatTimestamp(incident.startedAt)}`}
            // `min(95vw, 1400px)` lets the modal grow to ~95% of the viewport on
            // small screens (much friendlier than the previous 60vw which was
            // unreadable on mobile) and caps it at 1400px on wide displays so
            // the table doesn't stretch into uncomfortably long rows.
            customWidth="min(95vw, 1400px)"
            maxHeight="90vh"
        >
            <div className="flex flex-col gap-4">
                <GenericCard isCollapsible={false} className="min-w-0 w-full overflow-x-auto p-3 mb-5 py-3">
                    <div className="grid w-full  grid-cols-1 items-start gap-4 text-sm sm:grid-cols-3 sm:gap-3">
                        {/* 1: Status + Service ID | Service Name */}
                        <div className="flex min-w-0 w-full items-start gap-4">
                            <div className="flex min-w-0 flex-1 justify-start pl-1">
                                <div className="min-w-0 max-w-full overflow-hidden text-left">
                                    <div className="flex min-w-0 flex-col gap-1">
                                        <div className="flex min-w-0 flex-nowrap items-center gap-1 text-sm text-semantic-text-secondary">
                                            <span className="shrink-0">Service ID</span>
                                            <span className="shrink-0 text-semantic-text-tertiary" aria-hidden="true">|</span>
                                            <span className="shrink-0">Service Name</span>
                                        </div>
                                        {incident.affectedServices.length === 0 ? (
                                            <span className="text-semantic-text-secondary">{'\u2014'}</span>
                                        ) : (
                                            incident.affectedServices.map((svc) => (
                                                <div
                                                    key={svc.serviceType + String(svc.serviceId)}
                                                    className="flex min-w-0 flex-nowrap items-center gap-1 text-sm"
                                                >
                                                    <span className="min-w-0 shrink truncate font-mono text-[12px] text-semantic-text-primary" title={String(svc.subscriptionId)}>
                                                        {svc.subscriptionId}
                                                    </span>
                                                    <span className="shrink-0 text-semantic-text-tertiary" aria-hidden="true">|</span>
                                                    <span className="shrink-0">
                                                        {svc.serviceName ? (
                                                            <GenericTag label={svc.serviceName} variant="neutral" size="small" rounded="medium" />
                                                        ) : (
                                                            <span className="text-[12px] text-semantic-text-secondary">{'\u2014'}</span>
                                                        )}
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="shrink-0">
                                <span className="text-semantic-text-secondary">Status</span>
                                <div className="mt-0.5">
                                    <GenericTag label={incident.status} variant={STATUS_TAG_VARIANT[incident.status] || 'neutral'} size="small" rounded="medium" />
                                </div>
                            </div>
                        </div>
                        {/* 2: Duration + Events */}
                        <div className="flex min-w-0 w-full justify-start sm:justify-end">
                            <div className="flex flex-nowrap items-start gap-6 text-left">
                                <div className="min-w-0 whitespace-nowrap">
                                    <span className="block text-semantic-text-secondary">Duration</span>
                                    <p className="mt-0.5 font-mono text-[12px] leading-tight text-semantic-text-primary">
                                        {formatDuration(Math.max(0, (incident.resolvedAt ? new Date(incident.resolvedAt).getTime() : Date.now()) - new Date(incident.startedAt).getTime()))}
                                    </p>
                                </div>
                                <div className="min-w-0 whitespace-nowrap">
                                    <span className="block text-semantic-text-secondary">Events</span>
                                    <p className="mt-0.5 font-mono text-[12px] text-semantic-text-primary">{incident.eventCount}</p>
                                </div>
                            </div>
                        </div>

                        {/* 3: Tags — left-aligned on mobile, right-aligned on sm+ to sit at the end of the row */}
                        <div className="flex min-w-0 w-full flex-col items-start text-left sm:items-end sm:text-right">
                            <span className="text-semantic-text-secondary">Tags</span>
                            <div className="mt-0.5 flex w-full min-w-0 flex-wrap justify-start gap-1 sm:justify-end">
                                {(() => {
                                    const allTags = incident.affectedServices.flatMap((svc) => svc.tags || []);
                                    const uniqueTags = Array.from(new Set(allTags));
                                    if (uniqueTags.length === 0) return <span className="text-semantic-text-secondary">{'\u2014'}</span>;
                                    return uniqueTags.map((tag) => (
                                        <GenericTag key={tag} label={tag} variant="default" size="small" rounded="medium" />
                                    ));
                                })()}
                            </div>
                        </div>
                    </div>
                </GenericCard>

                <GenericTable<NetworkEventRow>
                    columns={eventDetailColumns}
                    data={incident.events}
                    rowKey={(row) => row.id}
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    width="100%"
                    tableClassName="bg-semantic-bg-primary"
                    maxTableBodyHeight="400px"
                />
            </div>
        </GenericModal>
    );
};

export default IncidentDetailModal;
